// frontend/src/pages/ExportVsStockPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import DateRangeControls from '../../components/DateRangeControls';
import DataTransformControls from '../../components/DataTransformControls';
import useDateRange from '../../hooks/useDateRange';
import { createDualAxisChartOptions } from '../../utils/chartOptions';
import { Select, Typography, Card, Space, Checkbox, Divider, Radio, Popover, Button, Switch, message, Tooltip } from 'antd';
import { 
    InfoCircleOutlined, 
    SearchOutlined, 
    WarningOutlined, 
    BulbOutlined, 
    CameraOutlined,      // PNG 아이콘
    FileExcelOutlined,   // Excel 아이콘
    FileTextOutlined     // CSV 아이콘
} from '@ant-design/icons';

import Loading from '../../components/Loading';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Title } = ResponsiveTypography;
const { Paragraph, Text } = Typography;

const ExportVsStockPage = () => {
    // --- 상태 관리 ---
    const [stockOptions, setStockOptions] = useState([]);
    const [fullItemOptions, setFullItemOptions] = useState([]);
    const [recommendedItemOptions, setRecommendedItemOptions] = useState([]);

    const [selectedStock, setSelectedStock] = useState("003230"); // 삼양식품
    const [selectedItem, setSelectedItem] = useState(null); 

    const [autoMapHsCode, setAutoMapHsCode] = useState(true);
    const [selectedCriteria, setSelectedCriteria] = useState("수출액");
    const [dataType, setDataType] = useState('Value');
    const [periodType, setPeriodType] = useState('TTM');
    const [isShifted, setIsShifted] = useState(true);
    
    const [chartData, setChartData] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true); 
    const [isChartLoading, setIsChartLoading] = useState(true); 

    // 차트 인스턴스 접근을 위한 Ref
    const chartRef = useRef(null);
    
    // --- Ref 및 커스텀 훅 ---
    const combinedDataForDateRange = useMemo(() => {
        const stockData = chartData?.stock?.data || [];
        const exportData = chartData?.export?.data || [];
        return stockData.concat(exportData);
    }, [chartData]);

    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(combinedDataForDateRange, { dateColumn: 'Date', defaultPeriod: '05Y' });

    // --- 종목명/품목명 표시용 Memo ---
    const { stockDisplayName, itemDisplayName } = useMemo(() => {
        const stockLabel = stockOptions.find(opt => opt.value === selectedStock)?.label || selectedStock || "";
        const itemLabel = (autoMapHsCode ? recommendedItemOptions : fullItemOptions).find(opt => opt.value === selectedItem)?.label || selectedItem || "";

        return {
            stockDisplayName: stockLabel.split(' (')[0],
            itemDisplayName: itemLabel.match(/\)\s(.*?)\s\//)?.[1] || itemLabel.split(' (')[0]
        };
    }, [selectedStock, selectedItem, stockOptions, fullItemOptions, recommendedItemOptions, autoMapHsCode]);

    // --- 파일 다운로드 핸들러 ---
    const getFileName = (ext) => {
        const dateStr = new Date().toISOString().split('T')[0];
        // 파일명: {종목명}_Vs_HSCode_{hscode}_{날짜}
        return `${stockDisplayName}_Vs_HSCode_${selectedItem}_${dateStr}.${ext}`;
    };

    const handleDownloadPNG = () => {
        if (chartRef.current) {
            const instance = chartRef.current.getEchartsInstance();
            const url = instance.getDataURL({
                type: 'png',
                pixelRatio: 2,
                backgroundColor: '#fff',
                excludeComponents: ['toolbox']
            });
            const link = document.createElement('a');
            link.href = url;
            link.download = getFileName('png');
            link.click();
        }
    };

    const handleDownloadData = (format) => {
        if (!chartData) return;

        // 데이터 병합 (날짜 기준)
        const stockMap = new Map(chartData.stock.data.map(d => [d.Date, d.price]));
        const exportMap = new Map(chartData.export.data.map(d => [d.Date, d.value]));
        
        const allDates = Array.from(new Set([...stockMap.keys(), ...exportMap.keys()])).sort();

        // [수정됨] 헤더 칼럼에 현재 데이터 타입(Value/Growth)과 주기(TTM/Monthly)를 명시
        let csvContent = `Date,Stock Price,Export Value (${dataType} - ${periodType})\n`;
        
        allDates.forEach(date => {
            const price = stockMap.get(date) || '';
            const value = exportMap.get(date) || '';
            csvContent += `${date},${price},${value}\n`;
        });

        const isExcel = format === 'excel';
        // Excel용 BOM(\uFEFF) 추가
        const blob = new Blob([isExcel ? '\uFEFF' + csvContent : csvContent], { 
            type: 'text/csv;charset=utf-8;' 
        });
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = getFileName('csv'); // Excel도 csv 포맷으로 오픈
        link.click();
    };

    // --- useEffect Hooks ---

    // 1. 초기화 Hook
    useEffect(() => {
        const initialize = async () => {
            setIsInitializing(true);
            try {
                const [stockRes, itemRes] = await Promise.all([
                    apiClient.get('/trade/stock-list'),
                    apiClient.get('/trade/export-item-list')
                ]);
                
                setStockOptions(stockRes.data);
                setFullItemOptions(itemRes.data);
                
                if (autoMapHsCode && selectedStock) {
                    const stockCode = selectedStock;
                    const res = await apiClient.get('/trade/stock2hscode_list', { params: { code: stockCode } });
                    const recommendedCodes = res.data.codes;
                    const newRecommendedOptions = itemRes.data.filter(opt => recommendedCodes.includes(opt.value));
                    
                    setRecommendedItemOptions(newRecommendedOptions);

                    if (newRecommendedOptions.length > 0) {
                        setSelectedItem(newRecommendedOptions[0].value);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch initial options:", error);
                message.error("초기 옵션 목록을 불러오는 데 실패했습니다.");
            } finally {
                setIsInitializing(false);
            }
        };
        initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2. HS Code 추천 Hook
    useEffect(() => {
        if (isInitializing || !autoMapHsCode) {
            return;
        }

        const fetchRecommendations = async () => {
            if (!selectedStock) return;
            try {
                const stockCode = selectedStock;
                
                const res = await apiClient.get('/trade/stock2hscode_list', { params: { code: stockCode } });
                const recommendedCodes = res.data.codes;
                const newRecommendedOptions = fullItemOptions.filter(opt => recommendedCodes.includes(opt.value));
                
                setRecommendedItemOptions(newRecommendedOptions);

                if (newRecommendedOptions.length > 0) {
                    setSelectedItem(newRecommendedOptions[0].value);
                } else {
                    const stockLabel = stockOptions.find(opt => opt.value === selectedStock)?.label || stockCode;
                    message.warning(`'${stockLabel.split(' (')[0]}'에 대해 추천되는 HS Code가 없습니다.`);
                    setSelectedItem(null); 
                }
            } catch (error) {
                console.error("Failed to fetch recommended HS codes:", error);
                message.error("HS Code 추천 목록을 가져오는 데 실패했습니다.");
            }
        };

        const timer = setTimeout(fetchRecommendations, 100);
        return () => clearTimeout(timer);

    }, [selectedStock, autoMapHsCode, isInitializing, fullItemOptions, stockOptions]);

    // 3. 차트 데이터 Fetching Hook
    useEffect(() => {
        if (!selectedStock || !selectedItem) {
            setChartData(null);
            setIsChartLoading(false);
            return;
        };
 
        const fetchChartData = async () => {
            setIsChartLoading(true);
            try {
                const res = await apiClient.get('/trade/stock-vs-export', {
                    params: {
                        stock_code: selectedStock,
                        hs_code: selectedItem,
                        criteria: selectedCriteria,
                        data_type: dataType.toLowerCase(),
                        period_type: periodType.toLowerCase(),
                        shift_days: isShifted,
                    }
                });
                setChartData(res.data);
            } catch (error) {
                console.error("Failed to fetch chart data:", error);
                message.error("차트 데이터를 불러오는 데 실패했습니다.");
                setChartData(null);
            } finally {
                setIsChartLoading(false);
            }
        };

        if (!isInitializing) {
            fetchChartData();
        }

    }, [selectedStock, selectedItem, selectedCriteria, dataType, periodType, isShifted, isInitializing]);
 
    // 4. 차트 옵션 생성 함수 (Tooltip 버그 수정 포함)
    const getChartOption = useCallback(() => {
        if (!isDateReady || !chartData) return {};
 
        const chartConfig = {
            categories: chartData.stock.data.map(d => d.Date),
            lineData: { name: chartData.stock.name, data: chartData.stock.data.map(d => [d.Date, d.price]) },
            barData: { name: chartData.export.name, data: chartData.export.data.map(d => [d.Date, d.value]) }
        };
 
        const options = createDualAxisChartOptions(chartConfig);
        const darkGrayColor = '#666';
 
        options.tooltip = {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: function (params) {
                if (!params || params.length === 0) return '';
                let tooltipHtml = `${params[0].axisValueLabel}<br/>`;
                
                params.forEach(param => {
                    const marker = `<span style="display:inline-block;margin-right:0.3125rem;border-radius:0.625rem;width:0.625rem;height:0.625rem;background-color:${param.color};"></span>`;
                    
                    // 값을 안전하게 추출하고 null 체크 수행
                    const val = (param.value && param.value.length > 1) ? param.value[1] : null;
                    let formattedValue = '';

                    if (val == null || isNaN(val)) {
                        formattedValue = '-'; 
                    } else {
                        if (param.seriesName === chartData.stock.name) {
                            formattedValue = `${val.toLocaleString('ko-KR')}원`;
                        } else if (param.seriesName === chartData.export.name) {
                            if (dataType === 'Value') {
                                formattedValue = `$${val.toLocaleString('en-US')}M`;
                            } else {
                                formattedValue = `${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
                            }
                        } else {
                            formattedValue = val.toLocaleString();
                        }
                    }
                    
                    tooltipHtml += `${marker} ${param.seriesName}: <strong>${formattedValue}</strong><br/>`;
                });
                return tooltipHtml;
            }
        };
        
        options.yAxis[0].name = `주가 (천원)`;
        options.yAxis[1].name = `수출 (${chartData.export.unit || '단위'})`;
        options.yAxis.forEach(axis => {
            axis.splitLine = { show: false };
            axis.nameTextStyle.color = darkGrayColor; 
            axis.axisLabel.color = darkGrayColor; 
            if (axis.axisLine && axis.axisLine.lineStyle) {
                axis.axisLine.lineStyle.color = darkGrayColor;
            }
        });
        options.yAxis[0].axisLabel.formatter = value => `${value / 1000}`;
        options.xAxis = {
            type: 'time',
            axisLabel: { color: darkGrayColor },
            axisLine: { lineStyle: { color: darkGrayColor } }
        };
        options.dataZoom = [
            { type: 'slider', show: true, xAxisIndex: [0], bottom: '3%', startValue: viewStartDate?.toISOString(), endValue: viewEndDate?.toISOString() },
            { type: 'inside', xAxisIndex: [0], disabled: true }
        ];
 
        return options;
    }, [isDateReady, chartData, viewStartDate, viewEndDate, dataType]);

    if (isInitializing && !chartData) {
        return <Loading />;
    }

    const InfoContent = () => (
        <div style={{ maxWidth: '15.625rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3125rem' }}>
                <Text style={{ flexShrink: 0 }}> 1. </Text>
                <Text>시총 "3천억원" 이상 종목, 수출액 "상위 2천개" 품목만 조회 가능합니다.</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.3125rem' }}>
                <Text style={{ flexShrink: 0 }}> 2. </Text>
                <Text>"10자리" HS code만 검색 가능합니다.</Text>
            </div>
        </div>
    );

    return (
        <div className="content-box">
            <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>
                품목별 수출 실적 vs 주가
            </Title>

           <Card style={{ marginTop: '1.25rem', marginBottom: '0.625rem' }} bodyStyle={{ padding: '0.625rem', background: '#F0F2F5' }}>
                <Title level={5} style={{ marginTop: 0 }}><BulbOutlined style={{ color: '#fae100' }}/>  조작 방법</Title>
                <Paragraph style={{ color: '#495057', marginLeft: "1.0rem"}}>
                    <ul>
                        <li>관심 기업을 아래 <code style={{borderWidth: '2px', borderColor: '#1890ff', background:'#ffffff', color: 'black' }}>종목/품목 찾기</code>에서 선택하면 구글 Gemini가 자동으로 HS Code를 매핑합니다.</li>
                        <li>직접 HS Code를 지정하려면 ‘자동 HS Code 매핑’ 기능을 해제한 뒤에, 'HS 코드' 또는 '품목명'을 입력하세요.</li>
                    </ul>
                </Paragraph>
            </Card>

            <Card style={{ marginTop: '0.625rem' }} bodyStyle={{ padding: '1.0rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <Space style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Text style={{fontSize:"1.2rem"}}><SearchOutlined /><code style={{borderWidth: '2px', borderColor: '#1890ff', background:'#ffffff', color: 'black', fontSize: '1.0rem' }}>종목/품목 찾기</code></Text>
                        <Text type="secondary" style={{ fontSize: '0.75rem' }}>(텍스트 입력 가능)</Text>
                        <Popover content={<InfoContent />} title={<><WarningOutlined /> 검색 제한사항</>} trigger="click" placement="topLeft">
                            <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ padding: '0 0.25rem', height: 'auto', color: '#1890ff' }} />
                        </Popover>
                    </Space>
                    <Select showSearch optionFilterProp="label" value={selectedStock} onChange={setSelectedStock} style={{ width: '100%' }} placeholder="주식 종목 (이름/코드)" options={stockOptions} loading={isInitializing} />
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginTop: '-0.625rem', marginBottom: '-0.625rem' }}>
                        <Text style={{ fontSize: '0.75rem' }}>자동 HS Code 매핑</Text>
                        <Switch checked={autoMapHsCode} onChange={setAutoMapHsCode} size="small" />
                    </div>
                    {/* HS Code Select - 공백 및 특수문자 무시 검색 로직 적용 */}
                    <Select
                        showSearch
                        filterOption={(input, option) => {
                            // 입력값과 라벨에서 영문, 숫자, 한글을 제외한 모든 문자(공백, ., - 등)를 제거하고 비교
                            const cleanInput = input.replace(/[^a-zA-Z0-9가-힣]/g, "").toLowerCase();
                            const cleanLabel = (option?.label ?? "").toString().replace(/[^a-zA-Z0-9가-힣]/g, "").toLowerCase();
                            return cleanLabel.includes(cleanInput);
                        }}
                        value={selectedItem}
                        onChange={setSelectedItem}
                        style={{ width: '100%' }}
                        placeholder={autoMapHsCode ? "종목을 선택하면 품목이 자동 추천됩니다" : "수출 품목 (이름/코드)"}
                        options={autoMapHsCode ? recommendedItemOptions : fullItemOptions}
                        notFoundContent={isChartLoading ? <span>로딩 중...</span> : (autoMapHsCode && selectedStock ? <span>추천 품목 없음</span> : <span>결과 없음</span>)}
                        disabled={isInitializing}
                    />
                </div>
            </Card>
            
            <Card style={{ marginTop: '1.0rem', padding: '0.625rem' }} bodyStyle={{ padding: '0rem' }}>
                {selectedStock && selectedItem ? (
                    <>
                        <Space style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                            <Title level={4}>"{stockDisplayName}" 주가</Title>
                            <Title level={5} style={{ marginLeft: '0.3125rem', marginRight: '0.3125rem' }}>vs</Title>
                            <Title level={4}>"{itemDisplayName}" 수출 실적</Title>
                        </Space>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '0.5rem', marginTop: '0.5rem' }}>
                            <Button.Group size="small">
                                <Tooltip title="차트 이미지 저장">
                                    <Button icon={<CameraOutlined />} onClick={handleDownloadPNG}>IMG</Button>
                                </Tooltip>
                                <Tooltip title="엑셀로 저장">
                                    <Button icon={<FileExcelOutlined style={{ color: '#1D6F42' }} />} onClick={() => handleDownloadData('excel')}>XLS</Button>
                                </Tooltip>
                                <Tooltip title="CSV로 저장">
                                    <Button icon={<FileTextOutlined style={{ color: '#217346' }} />} onClick={() => handleDownloadData('csv')}>CSV</Button>
                                </Tooltip>
                            </Button.Group>
                        </div>

                        <Divider style={{ marginTop: '0.3125rem', marginBottom: '0.3125rem' }} />
                        
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem', marginTop: '0.625rem', padding: '0 0.5rem' }}>
                            <Radio.Group value={selectedCriteria} onChange={(e) => setSelectedCriteria(e.target.value)} size="small">
                                <Radio.Button value="수출액">수출액</Radio.Button>
                                <Radio.Button value="수출무게">수출무게</Radio.Button>
                            </Radio.Group>
                            
                            <DataTransformControls dataType={dataType} periodType={periodType} onDataTypeChange={setDataType} onPeriodTypeChange={setPeriodType} size="small" />
                        </div>

                        <ReactECharts 
                            ref={chartRef} 
                            option={getChartOption()} 
                            style={{ height: '25rem' }} 
                            showLoading={isChartLoading} 
                        />
                        
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', marginTop: '1rem', marginBottom: '1rem', marginLeft: '1.0rem', marginRight: '1.0rem' }}>
                            <Checkbox checked={isShifted} onChange={e => setIsShifted(e.target.checked)}>수출 데이터 15일 시프트</Checkbox>
                        </div>
                        <Divider style={{ marginTop: '0.3125rem', marginBottom: '1.25rem' }} />
                        <DateRangeControls minDate={minDate} maxDate={maxDate} viewStartDate={viewStartDate} viewEndDate={viewEndDate} onPeriodChange={handlePeriodChange} onDateChange={handleDateChange} />
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '1.25rem' }}>
                        <Text>종목에 매핑된 HS 코드가 없습니다. <br/>종목을 다시 선택하거나, 자동 HS Code 매핑을 해제하고 직접 HS코드를 지정하세요. {isChartLoading ? '차트를 불러오는 중입니다...' : ''}</Text>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default ExportVsStockPage;